import { Link } from '@dev-spendesk/grapes';
import { ViewportPortal, useReactFlow } from '@xyflow/react';
import { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { LocalStorageKey } from 'src/core/constants/storage';
import { openSupportChat } from 'src/core/utils/supportChat';

import { Tooltip, type TooltipProps } from './Tooltip';
import { useWorkflowContext } from '../../state/WorkflowContext';

export const ApprovalWorkflowTour = () => {
  const reactFlow = useReactFlow();
  const [isTourOpen, setIsTourOpen] = useLocalStorage(
    LocalStorageKey.ApprovalWorkflowTour,
    true
  );
  const { t } = useTranslation('global');

  const { nodes } = useWorkflowContext();
  const [tourStep, setTourStep] = useState<number>(0);

  const newConditionNode = nodes.find(
    (node) => node.type === 'newConditionNode',
  );
  const standardApprovalFlowNode = nodes.find(
    (node) => node.type === 'standardApprovalFlowNode',
  );

  useEffect(() => {
    if (newConditionNode && standardApprovalFlowNode && isTourOpen) {
      zoomTo(newConditionNode.position.x, newConditionNode.position.y);
    }
  }, [newConditionNode, standardApprovalFlowNode, isTourOpen]);

  if (!isTourOpen) {
    return null;
  }

  if (!newConditionNode || !standardApprovalFlowNode) {
    return null;
  }

  const zoomTo = (x: number, y: number) => {
    const shiftX = 200;
    const shiftY = 100;
    reactFlow.setCenter(x + shiftX, y + shiftY, { zoom: 0.9, duration: 1000 });
  };

  const createNewFlowsTooltipTargetDimensions = {
    // these are custom values because we need to position this tooltip this way
    x: newConditionNode.position.x + 320,
    y: newConditionNode.position.y + 200,
    width: 50,
    height: 80,
  };

  const handleSkip = () => {
    setIsTourOpen(false);
  };

  const tourItems: TooltipProps[] = [
    {
      title: t('approvalWorkflowTour.newApprovalWorkflowBuilder.title'),
      content: t('approvalWorkflowTour.newApprovalWorkflowBuilder.content'),
      step: 1,
      onNext: () => {
        setTourStep(1);
        zoomTo(
          standardApprovalFlowNode.position.x,
          standardApprovalFlowNode.position.y,
        );
      },
      onSkip: () => {
        handleSkip();
      },
      placement: 'top',
      isVisible: tourStep === 0,
      target: {
        width: 50,
        height: 80,
        x: newConditionNode.position.x,
        y: newConditionNode.position.y,
      },
    },
    {
      title: t('approvalWorkflowTour.defaultApprovers.title'),
      content: t('approvalWorkflowTour.defaultApprovers.content'),
      step: 2,
      placement: 'bottom',
      onNext: () => {
        setTourStep(2);
        zoomTo(
          createNewFlowsTooltipTargetDimensions.x,
          createNewFlowsTooltipTargetDimensions.y,
        );
      },
      onPrevious: () => {
        setTourStep(0);
        zoomTo(newConditionNode.position.x, newConditionNode.position.y);
      },
      onSkip: () => {
        handleSkip();
      },
      isVisible: tourStep === 1,
      target: {
        width: 220,
        height: 94,
        x: standardApprovalFlowNode.position.x,
        y: standardApprovalFlowNode.position.y,
      },
    },
    {
      title: t('approvalWorkflowTour.createNewFlows.title'),
      content: (
        <div className="flex flex-col gap-8">
          <p>
            <button
              className="m-0 cursor-pointer border-none bg-background-complementary-default p-0 text-content-complementary underline body-m"
              onClick={() => openSupportChat()}
              type="button"
            >
              {t('approvalWorkflowTour.createNewFlows.contactUs')}
            </button>{' '}
            {t('approvalWorkflowTour.createNewFlows.spendType')}
          </p>
          <p>
            <Link target="_blank" href="https://helpcenter.spendesk.com/articles/10539598-approval-workflows#h_1dfd5d7510">
              {t('approvalWorkflowTour.createNewFlows.learnMore')}
            </Link>{' '}
            {t('approvalWorkflowTour.createNewFlows.discoverInsights')}
          </p>
        </div>
      ),
      step: 3,
      placement: 'right',
      onPrevious: () => {
        setTourStep(1);
        zoomTo(
          standardApprovalFlowNode.position.x,
          standardApprovalFlowNode.position.y,
        );
      },
      isLastStep: true,
      onSkip: () => {
        handleSkip();
      },
      isVisible: tourStep === 2,
      target: createNewFlowsTooltipTargetDimensions,
    },
  ];

  return (
    <ViewportPortal>
      <div className="pointer-events-none">
        {tourItems.map((item) => (
          <Tooltip
            key={item.step}
            isLastStep={item.isLastStep}
            title={item.title}
            content={item.content}
            step={item.step}
            onNext={item.onNext}
            onPrevious={item.onPrevious}
            onSkip={item.onSkip}
            target={item.target}
            isVisible={item.isVisible}
            placement={item.placement}
          />
        ))}
      </div>
    </ViewportPortal>
  );
};
